/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react'
import { formatPrice, withOrdinalSuffix } from 'helpers'
import { useTranslation } from 'hooks'
import PricingGuidanceSlider from '../../selling/common/PricingGuidanceSlider'
import { PricingSuggestion, MINIMUM_LIST_PRICE } from '../../selling/common'
import * as S from './InlinePriceAdjust.styles'

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

export interface PriceAdjustContentProps {
  suggestion: PricingSuggestion | null
  minimumPrice: number
  lowestQueuePrice: number | null
  currentPriceInCents: number
  currencyCode: string
  selectedPrice: number | null
  onPriceSelect: (price: number) => void
  onMatchLastSold: () => void
  onSave: () => void
  saveState: SaveState
  loading: boolean
  sellerId?: number
  productId?: number
  queuePosition?: number | null
  lowestPriceCurrency?: string
  handlingFee?: number
  paymentFee?: number
  shippingFee?: number
}

const PriceAdjustContent: React.FC<PriceAdjustContentProps> = ({
  suggestion,
  minimumPrice,
  lowestQueuePrice,
  currentPriceInCents,
  currencyCode,
  selectedPrice,
  onPriceSelect,
  onMatchLastSold,
  onSave,
  saveState,
  loading,
  queuePosition,
  lowestPriceCurrency,
  handlingFee,
  paymentFee,
  shippingFee
}) => {
  const { t } = useTranslation()
  const [showManualInput, setShowManualInput] = React.useState(false)
  const [manualValue, setManualValue] = React.useState('')

  // Calculate payout client-side (same approach as SalePriceBreakdown)
  const effectivePrice = selectedPrice ?? currentPriceInCents / 100
  const payoutEstimate = React.useMemo(() => {
    if (effectivePrice == null || effectivePrice <= 0) return null
    if (handlingFee == null || paymentFee == null) return null

    const totalFeePercent = handlingFee + paymentFee
    const totalFees = effectivePrice * (totalFeePercent / 100)
    const shipping = shippingFee ?? 0
    const payout = Math.max(effectivePrice - totalFees - shipping, 0)

    return formatPrice(payout, { currencyCode })
  }, [effectivePrice, handlingFee, paymentFee, shippingFee, currencyCode])

  const handleManualSubmit = React.useCallback(() => {
    const parsed = parseFloat(manualValue)

    if (!isNaN(parsed) && parsed > 0) {
      onPriceSelect(parsed)
      setShowManualInput(false)
    }
  }, [manualValue, onPriceSelect])

  const handleManualKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleManualSubmit()
      }
    },
    [handleManualSubmit]
  )

  const currentMajor = currentPriceInCents / 100
  const hasChange =
    selectedPrice !== null &&
    Math.round(selectedPrice * 100) !== currentPriceInCents
  const lastSoldPrice = suggestion?.price ?? null

  const saveLabel =
    saveState === 'saving'
      ? 'Saving...'
      : saveState === 'error'
      ? 'Failed — try again'
      : hasChange
      ? `Save ${formatPrice(selectedPrice!, { currencyCode })}`
      : 'Save'

  // Build queue status message
  let queueMessage: string | null = null

  if (queuePosition != null) {
    if (queuePosition > 3) {
      queueMessage = t('react_components.selling.at_least_three_ahead')
    } else {
      queueMessage = t('react_components.selling.your_item_will_be', {
        place: withOrdinalSuffix(queuePosition),
        escapeValue: false
      })
    }

    if (lowestQueuePrice != null && lowestQueuePrice > 0) {
      const lowestFormatted = formatPrice(lowestQueuePrice, {
        currencyCode: lowestPriceCurrency || currencyCode
      })

      if (queuePosition <= 3) {
        queueMessage += ` ${t(
          'react_components.selling.lowest_price_for_conversion',
          {
            formatted_price: lowestFormatted,
            conversion: t('shared.conversion.sizes')
          }
        )}`
      } else {
        queueMessage += ` ${t('react_components.selling.match_or_beat', {
          formatted_price: lowestFormatted
        })}`
      }
    }
  }

  return (
    <>
      {loading && !queueMessage ? (
        <S.QueueRow style={{ opacity: 0.5 }}>
          {t('react_components.selling.checking_placement')}
        </S.QueueRow>
      ) : queueMessage ? (
        <S.QueueRow dangerouslySetInnerHTML={{ __html: queueMessage }} />
      ) : null}

      <PricingGuidanceSlider
        suggestion={suggestion}
        minimumPrice={minimumPrice || MINIMUM_LIST_PRICE}
        lowestQueuePrice={lowestQueuePrice}
        initialValue={selectedPrice ?? currentMajor}
        currencyCode={currencyCode}
        onPriceSelect={onPriceSelect}
        rangePercent={0.2}
        snapPoints={lastSoldPrice ? [lastSoldPrice] : undefined}
        snapThreshold={2}
      />

      <S.PriceInputRow>
        <S.PriceInputLabel>
          {showManualInput
            ? t('react_components.pricing_tasks.enter_price')
            : t('react_components.pricing_tasks.custom_price')}
        </S.PriceInputLabel>
        {showManualInput ? (
          <S.PriceInput
            type="number"
            step="1"
            min="1"
            placeholder={formatPrice(currentMajor, { currencyCode })}
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            onKeyDown={handleManualKeyDown}
            onBlur={handleManualSubmit}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        ) : (
          <S.PriceInputLabel
            as="button"
            onClick={() => {
              setManualValue('')
              setShowManualInput(true)
            }}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              padding: 0
            }}
          >
            {t('react_components.pricing_tasks.type_price')}
          </S.PriceInputLabel>
        )}
      </S.PriceInputRow>

      {payoutEstimate && (
        <S.PayoutRow>
          <S.PayoutLabel>
            {t('react_components.pricing_tasks.estimated_payout')}
          </S.PayoutLabel>
          <S.PayoutValue>{payoutEstimate}</S.PayoutValue>
        </S.PayoutRow>
      )}

      <S.ButtonRow>
        {lastSoldPrice && (
          <S.MatchLastSoldButton type="button" onClick={onMatchLastSold}>
            Match Last Sold {formatPrice(lastSoldPrice, { currencyCode })}
          </S.MatchLastSoldButton>
        )}
        <S.SaveButton
          type="button"
          variant={
            saveState === 'saving'
              ? 'saving'
              : saveState === 'error'
              ? 'error'
              : 'default'
          }
          disabled={!hasChange || saveState === 'saving'}
          onClick={onSave}
        >
          {saveLabel}
        </S.SaveButton>
      </S.ButtonRow>
    </>
  )
}

export default PriceAdjustContent
